<template>
  <div class="relative my-0 mx-auto">
    <Form
      v-if="initData != null"
      ref="editProfileForm"
      :validateOnValueUpdate="false"
      :resolver="resolver"
      :validateOnBlur="true"
      @submit="saveChanges"
      :validate-on-value-update="onFormValuesChange"
      :initialValues="initData"
      class="card max-w-[90vw] w-[600px] h-full"
    >
      <image-upload-component name="picture_profile.medium"
        :initial-image="initData.picture_profile.medium"/>
      <FormField v-slot="$field" as="section" name="firstName"
        class="flex flex-col gap-1 w-full">
        <IftaLabel class="mb-3">
          <InputText type="text" size="medium" :invalid="$field?.invalid"
            :placeholder="$t('RegisterComponent.placeholders.firstName')" class="w-full"
            data-testid="txt_firstName"/>
          <label for="txt_description"> {{ $t("EditProfileComponent.firstName") }}</label>
        </IftaLabel>
        <Message v-if="$field?.invalid" severity="error" size="small"
          variant="simple">{{ $field.error?.message }}</Message>
      </FormField>
      <FormField v-slot="$field" as="section" name="lastName"
        class="flex flex-col gap-1 ">
        <IftaLabel class="mb-3 w-full">
          <InputText type="text" size="medium" :invalid="$field?.invalid" class="w-full"
            :placeholder="$t('RegisterComponent.placeholders.lastName')"
            data-testid="txt_lastName"/>
          <label for="txt_description"> {{ $t("EditProfileComponent.lastName") }}</label>
        </IftaLabel>
        <Message v-if="$field?.invalid" severity="error" size="small"
          variant="simple">{{ $field.error?.message }}</Message>
      </FormField>
      <FormField v-slot="$field" as="section" name="email"
        class="flex flex-col gap-1 ">
        <IftaLabel class="mb-3 w-full">
          <InputText type="text" size="medium" :invalid="$field?.invalid" class="w-full"
            :placeholder="$t('RegisterComponent.placeholders.email')" data-testid="txt_email"/>
          <label for="txt_description"> {{ $t("EditProfileComponent.email") }}</label>
        </IftaLabel>
        <Message v-if="$field?.invalid" severity="error" size="small"
          variant="simple">{{ $field.error?.message }}</Message>
      </FormField>
      <FormField v-slot="$field" as="section" name="mobileNumber"
        class="flex flex-col gap-1 ">
        <IftaLabel class="mb-3 w-full">
          <InputText type="text" size="medium" :invalid="$field?.invalid" class="w-full"
            :placeholder="$t('RegisterComponent.placeholders.mobileNumber')"
            data-testid="txt_mobileNumber"/>
          <label for="txt_description"> {{ $t("EditProfileComponent.mobileNumber") }}</label>
        </IftaLabel>
        <Message v-if="$field?.invalid" severity="error" size="small"
          variant="simple">{{ $field.error?.message }}</Message>
      </FormField>
      <p class="font-semibold">{{ $t('EditProfileComponent.NotificationSettings') }}</p>
      <p class="text-base">
        {{ $t('EditProfileComponent.NotificationSettingsText') }}
      </p>
      <FormField v-slot="$field" as="section" name="SMSallowed" class="flex gap-2 mt-1">
        <Checkbox v-model='$field.value' binary size="large" data-testid="chk_smsAllowed"/>
        <label for="SMSallowed" v-tooltip="$t('EditProfileComponent.receiveSmsTooltip')">
        {{ $t('EditProfileComponent.receiveSmsText') }}
        </label>
      </FormField>
      <FormField v-slot="$field" as="section" name="pushAllowed" class="flex gap-2 mt-2">
        <Checkbox v-model='$field.value' binary size="large" data-testid="chk_pushAllowed"/>
        <label for="pushAllowed" v-tooltip="$t('EditProfileComponent.receivePushTooltip')">
        {{ $t('EditProfileComponent.receivePushText') }}
        </label>
      </FormField>
      <FormField v-slot="$field" as="section" name="mailAllowed" class="flex gap-2 mt-2">
        <Checkbox v-model='$field.value' binary size="large" data-testid="chk_mailAllowed"/>
        <label for="mailAllowed" v-tooltip="$t('EditProfileComponent.receiveMailTooltip')">
        {{ $t('EditProfileComponent.receiveMailText') }}
        </label>
      </FormField>
      <br>
      <p class="font-semibold">
        {{ $t('EditProfileComponent.PauseNotificationsText') }}
      </p>
      <FormField v-slot="$field" as="section" name="workingDayAllowed" class="flex flex-col gap-2 mt-3">
        <div class="flex gap-2">
          <Checkbox v-model="$field.value" binary size="large" data-testid="chk_workingDayAllowed"/>
          <label for="workingDayAllowed">
          <span v-html="$t('EditProfileComponent.PauseNotificationsWorkingDayText')" />
          </label>
        </div>
        <div v-if="$field.value" class="flex flex-row gap-2 items-center justify-center">
          <FormField v-slot="$startField" as="section" name="workingDayStart" class="w-20">
            <DatePicker v-model="$startField.value" hourFormat="24" timeOnly :manual-input="false"
              data-testid="date_workingDayStart"/>
          </FormField>
          <span>{{ $t('EditProfileComponent.and') }}</span>
          <FormField v-slot="$endField" as="section" name="workingDayEnd" class="w-20">
            <DatePicker v-model="$endField.value" hourFormat="24" timeOnly :manual-input="false"
              data-testid="date_workingDayEnd"/>
          </FormField>
        </div>
        <p v-if="$field.value" class="text-sm text-surface-500 mt-1">
          {{ $t('EditProfileComponent.PauseNotificationsHint') }}
        </p>
      </FormField>
      <FormField v-slot="$field" as="section" name="weekendAllowed" class="flex gap-2 mt-3">
        <Checkbox v-model='$field.value' binary size="large" data-testid="chk_workendAllowed"/>
        <label for="weekendAllowed"
          v-html="$t('EditProfileComponent.PauseNotificationsWeekendsText')">
        </label>
      </FormField>
      <p class="font-semibold mt-4">{{ $t('EditProfileComponent.TravelBuddy', {text: featureName}) }}</p>
      <FormField v-slot="$field" as="section" name="travelBuddyOnHoliday" class="flex gap-2 mt-2">
        <Checkbox v-model="$field.value" binary size="large"
          data-testid="chk_travelBuddyOnHoliday"/>
        <label for="travelBuddyOnHoliday">
          <span v-html="$t('EditProfileComponent.travelBuddyOnHolidayLabel', {text: featureName})"></span>
        </label>
      </FormField>
      <FormField v-slot="$field" as="section" name="preferredLanguage" class="py-3">
        <span :class="getFlagIcon($field.value)" class="mr-1"/>
        <label class="text-base pr-3" for="preferredLanguage">
        {{ $t('EditProfileComponent.chooseLang') }}
        </label>
        <Select v-model="$field.value" :options="language" optionLabel="name"
          optionValue ="value" :invalid="$field?.invalid" data-testid="sel_language"/>
      </FormField>
      <FormField v-slot="$field" as="section" name="timezone" class="py-3 flex flex-col gap-1">
        <label class="text-base" for="sel_timezone">
          {{ $t('EditProfileComponent.timezone') }}
        </label>
        <Select
          v-model="$field.value"
          :options="timezoneOptions"
          filter
          :placeholder="$t('EditProfileComponent.timezonePlaceholder')"
          :invalid="$field?.invalid"
          :disabled="timezoneAutoFromDevice"
          class="w-full"
          data-testid="sel_timezone"
        />
        <Message v-if="$field?.invalid" severity="error" size="small"
          variant="simple">{{ $field.error?.message }}</Message>
      </FormField>
      <FormField v-slot="$field" as="section" name="timezoneAutoFromDevice" class="flex gap-2 mt-1 mb-2">
        <Checkbox
          v-model="$field.value"
          binary
          size="large"
          data-testid="chk_timezoneAutoFromDevice"
          @update:modelValue="onTimezoneAutoFromDeviceChange"
        />
        <label for="timezoneAutoFromDevice">
          <span>{{ $t('EditProfileComponent.timezoneAutoFromDevice') }}</span>
        </label>
      </FormField>
      <div class="flex flex-row justify-around relative box-border my-8 mx-0 text-black text-xl">
        <Button unstyled type="button" name="cancel" id="btn_cancel" class="btn-secondary w-full sm:w-60 min-h-10 py-2 px-4 text-base sm:text-xl" 
          data-testid="btn_cancel" @click="cancelChanges">
        {{ $t('EditProfileComponent.cancel') }}
        </Button>
        <Button unstyled type="submit" name="save" id="btn_save" class="btn-primary-accent w-full sm:w-60 min-h-10 py-2 px-4 text-base sm:text-xl"
          :class="{'cursor-not-allowed'
            : (isSubmitting || !hasFormChanged)}" :disabled="isSubmitting || !hasFormChanged"
          data-testid="btn_save">
        {{ $t('EditProfileComponent.saveChanges') }}
        </Button>
      </div>
    </Form>
  </div>
  <provide-feedback-component></provide-feedback-component>
</template>

<script>
import ImageUploadComponent from '@/components/Home/Shared/ImageUploadComponent.vue';
import ProvideFeedbackComponent from '@/components/global/ProvideFeedbackComponent.vue';
import { Form, FormField } from '@primevue/forms';
import {
  Button,
  Checkbox,
  DatePicker,
  IftaLabel,
  InputText,
  Message,
  Select,
} from 'primevue';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import { ref } from 'vue';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';
import {
  getBrowserTimezone,
  getTimezoneOptions,
  resolveTimezoneForForm,
} from '@/helpers/timezoneOptions';

/**
 * Working-day pause times as HH:mm wall-clock for `user.timezone` on the backend.
 * Do not use getUTCHours / setUTCHours — that shifts digits by the browser offset.
 */
function formatWallClockHhMm(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function parseWallClockHhMm(hhmm) {
  if (typeof hhmm !== 'string' || !hhmm.trim()) return null;
  const parts = hhmm.trim().split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parts.length > 1 ? parseInt(parts[1], 10) : 0;
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }
  const local = new Date();
  local.setHours(hours, minutes, 0, 0);
  return local;
}

export default {
  name: 'EditProfileComponent',
  components: {
    ImageUploadComponent,
    ProvideFeedbackComponent,
    Form,
    FormField,
    InputText,
    Message,
    IftaLabel,
    Button,
    Select,
    Checkbox,
    DatePicker,
  },
  data() {
    return {
      initData: null,
      isSubmitting: false,
      hasFormChanged: false,
      originalFormData: null,
      timezoneOptions: getTimezoneOptions(),
      timezoneAutoFromDevice: false,
      editProfileBreadCrumbs: [
        {
          route: '/edit-profile',
          isActive: true,
          label: this.$t('BreadCrumbs.editProfile'),
        },
      ],
      language: ref([
        { name: 'English', value: 'en' },
        { name: 'Deutsch', value: 'de' },
      ]),
    };
  },
  computed: {
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'travelBuddyHistory',
        this.$t('FallbackTexts.travelBuddyHistory'),
        this.$i18n?.locale,
      );
    },
    resolver() {
      return zodResolver(
        z.object({
          firstName: z.string()
            .min(2, this.$t('RegisterComponent.validation.firstNameMin'))
            .max(100, this.$t('RegisterComponent.validation.firstNameMax'))
            .regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s'.-]+$/, this.$t('RegisterComponent.validation.firstNameFormat')),
          lastName: z.string()
            .min(2, this.$t('RegisterComponent.validation.lastNameMin'))
            .max(100, this.$t('RegisterComponent.validation.lastNameMax'))
            .regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s'.-]+$/, this.$t('RegisterComponent.validation.lastNameFormat')),
          email: z.string()
            .min(1, this.$t('RegisterComponent.validation.emailRequired'))
            .email(this.$t('RegisterComponent.validation.emailFormat')),
          mobileNumber: z.string()
            .optional()
            .or(z.literal(''))
            .or(z.literal(null)),
          SMSallowed: z.boolean().optional(),
          pushAllowed: z.boolean().optional(),
          mailAllowed: z.boolean().optional(),
          workingDayAllowed: z.boolean().optional(),
          workingDayStart: z
            .date()
            .or(z.nullable(z.string()))
            .optional(),
          workingDayEnd: z
            .date()
            .or(z.nullable(z.string()))
            .optional(),
          weekendAllowed: z.boolean().optional(),
          preferredLanguage: z.enum(['en', 'de'], {
            required_error: this.$t('EditProfileComponent.validation.preferredLanguageRequired'),
            invalid_type_error: this.$t('EditProfileComponent.validation.preferredLanguageInvalid'),
          }),
          timezone: z.string()
            .min(1, this.$t('EditProfileComponent.validation.timezoneRequired'))
            .max(100, this.$t('EditProfileComponent.validation.timezoneMax')),
          timezoneAutoFromDevice: z.boolean().optional(),
          travelBuddyOnHoliday: z.boolean().optional(),
        }),
      );
    },
  },
  methods: {
    formatWallClockHhMm,
    parseWallClockHhMm,
    onTimezoneAutoFromDeviceChange(value) {
      const enabled = value === true;
      this.timezoneAutoFromDevice = enabled;
      // When enabling device sync, show the IANA zone that will be saved (FA-06.08 preview).
      if (enabled) {
        const deviceTz = getBrowserTimezone();
        const form = this.$refs.editProfileForm;
        if (form && typeof form.setFieldValue === 'function') {
          form.setFieldValue('timezone', deviceTz);
        }
      }
    },
    onFormValuesChange(values) {
      // Compare current values with original data to detect changes
      this.hasFormChanged = this.hasFormDataChanged(values);
    },
    hasFormDataChanged(currentValues) {
      if (!this.originalFormData) return false;

      // Compare each field to detect changes
      const fieldsToCompare = ['firstName', 'lastName', 'email', 'mobileNumber', 'SMSallowed',
        'pushAllowed', 'mailAllowed', 'workingDayAllowed', 'weekendAllowed', 'preferredLanguage',
        'timezone', 'timezoneAutoFromDevice', 'travelBuddyOnHoliday'];

      // Use array.some() instead of for...of loop
      return fieldsToCompare.some((field) => {
        if (field === 'timezoneAutoFromDevice') {
          return Boolean(currentValues[field]) !== Boolean(this.originalFormData[field]);
        }
        const currentValue = currentValues[field] || '';
        const originalValue = this.originalFormData[field] || '';

        // Handle different data types
        if (field === 'workingDayStart' || field === 'workingDayEnd') {
          // For date fields, compare timestamps (treat two invalid dates as equal)
          const currentTime = currentValue ? new Date(currentValue).getTime() : null;
          const originalTime = originalValue ? new Date(originalValue).getTime() : null;
          if (Number.isNaN(currentTime) && Number.isNaN(originalTime)) return false;
          return currentTime !== originalTime;
        }
        // For other fields, direct comparison
        return currentValue !== originalValue;
      });
    },
    async saveChanges(values) {
      if (values.valid) {
        const params = { ...values.values };

        if (params.workingDayAllowed === true) {
          if (!params.workingDayStart) {
            this.$toast.add({
              severity: 'error',
              summary: this.$t('PauseNotificationComponent.toast.requiredStartTime'),
              detail: '',
              life: 3000,
            });
            return;
          }
          if (!params.workingDayEnd) {
            this.$toast.add({
              severity: 'error',
              summary: this.$t('PauseNotificationComponent.toast.requiredEndTime'),
              detail: '',
              life: 3000,
            });
            return;
          }
          if (params.workingDayStart && params.workingDayEnd) {
            const startDate = new Date(params.workingDayStart);
            const endDate = new Date(params.workingDayEnd);

            const startTime = startDate.getHours() * 60 + startDate.getMinutes();
            const endTime = endDate.getHours() * 60 + endDate.getMinutes();

            if (endTime <= startTime) {
              this.$toast.add({
                severity: 'error',
                summary: this.$t('PauseNotificationComponent.toast.timeError'),
                detail: '',
                life: 3000,
              });
              return;
            }
            params.workingDayStart = this.formatWallClockHhMm(startDate);
            params.workingDayEnd = this.formatWallClockHhMm(endDate);
          } else {
            params.workingDayStart = null;
            params.workingDayEnd = null;
          }
        } else {
          params.workingDayStart = null;
          params.workingDayEnd = null;
        }

        params.timezone = resolveTimezoneForForm(params.timezone);
        if (params.timezoneAutoFromDevice === true) {
          params.timezone = getBrowserTimezone();
          params.timezoneManual = false;
        } else {
          params.timezoneManual = true;
        }
        delete params.timezoneAutoFromDevice;
        /* params.picture_profile = {
          large: this.initData.picture_profile.large,
          medium: this.initData.picture_profile.medium,
          small: this.initData.picture_profile.small,
        }; */
        try {
          this.isSubmitting = true;
          // Ensure preferredLanguage is a valid locale string
          const validLocale = params.preferredLanguage && ['en', 'de'].includes(params.preferredLanguage)
            ? params.preferredLanguage
            : 'en';
          this.$i18n.locale = validLocale;
          this.$store.commit('setLanguage', validLocale);
          await this.$store.dispatch('updateProfile', params);
          this.isSubmitting = false;
          this.hasFormChanged = false; // Reset change tracking after successful save
          this.originalFormData = { ...params }; // Update original data
          this.$toast.add({
            severity: 'success',
            summary: this.$t('EditProfileComponent.profileSaved'),
            detail: '',
            life: 3000,
          });
        } catch (error) {
          this.isSubmitting = false;
          this.$toast.add({
            severity: 'error',
            summary: this.$t('EditProfileComponent.errorSaved'),
            detail: '',
            life: 3000,
          });
        }
      }
    },
    cancelChanges() {
      this.$router.push('/');
    },
    getFlagIcon(key) {
      if (key === 'de') {
        // German flag
        return 'fi fi-de';
      }

      // Great Britain flag
      return 'fi fi-gb';
    },
  },
  async mounted() {
    try {
      let data = this.$store.getters.getUser;
      if (!data?.userId) {
        await this.$store.dispatch('getUserInfo');
        data = this.$store.getters.getUser;
      }
      if (data && data.SMSallowed !== false) data.SMSallowed = true;
      if (data && data.pushAllowed !== false) data.pushAllowed = true;
      if (data && data.mailAllowed !== false) data.mailAllowed = true;
      if (data && data.workingDayAllowed !== false) data.workingDayAllowed = true;
      if (data && data.workingDayStart) {
        const startDate = this.parseWallClockHhMm(data.workingDayStart);
        data.workingDayStart = startDate && !Number.isNaN(startDate.getTime()) ? startDate : null;
      }
      if (data && data.workingDayEnd) {
        const endDate = this.parseWallClockHhMm(data.workingDayEnd);
        data.workingDayEnd = endDate && !Number.isNaN(endDate.getTime()) ? endDate : null;
      }
      if (data && !data.picture_profile) {
        data.picture_profile = {
          large: '',
          medium: '',
          small: '',
        };
      }
      data.travelBuddyOnHoliday = data.onHoliday ?? false;
      data.timezone = resolveTimezoneForForm(data.timezone);
      data.timezoneAutoFromDevice = data.timezoneManual !== true;
      this.timezoneAutoFromDevice = data.timezoneAutoFromDevice;

      this.initData = data;
      // Store original form data for comparison
      this.originalFormData = { ...data };
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: error.response?.data?.message,
        detail: '',
        life: 3000,
      });
    }
  },
};
</script>
